import React, { useEffect, useState } from "react";
import {
  Building2,
  ChevronDown,
  ChevronUp,
  Eye,
  Pencil,
  Plus,
  Power,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "../../../components/ui/Badge";
import Modal from "src/components/ui/Modal";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import { TenantCreateWizard } from "./TenantForm";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantActions from "src/redux/slices/tenantSlice";
import { formatDateTime } from "src/utils/date-time";
import { formatAddress } from "src/utils/formatAddress";
import { toast } from "react-toastify";
import { TenantPlanSnapshot } from "src/types/tenant.types";

const getActivePlan = (plans: TenantPlanSnapshot[] | undefined) => {
  if (!plans?.length) return null;
  const now = new Date();
  const sortedPlans = [...plans].sort((a, b) => {
    const aDate = a.planStartsAt || a.createdAt || "";
    const bDate = b.planStartsAt || b.createdAt || "";
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });
  return (
    sortedPlans.find((plan) =>
      plan.planEndsAt ? new Date(plan.planEndsAt) >= now : false
    ) || sortedPlans[0]
  );
};

const getPlanStatus = (planEndsAt?: string) => {
  if (!planEndsAt) return "active";
  return new Date(planEndsAt) < new Date() ? "expired" : "active";
};

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
  { label: "Draft", value: "draft" },
];

const TenantListPage: React.FC = () => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<any | null>(null);
  const [limit, setLimit] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const { editTenant, tenantList, createTenant } = useAppSelector(
    (state) => state.tenant
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const hasActiveFilters = Boolean(search || statusFilter);

  // Debounce the search box before it hits the API
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getTenantList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, limit]);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editTenant.data) {
      toast.success("Tenant modified successfully");
      dispatch(tenantActions.resetEditTenant());
      handleStatusModalClose();
      getTenantList(1);
    } else if (editTenant.error) {
      toast.error(`Error: ${editTenant.error}`);
      handleStatusModalClose();
    }
  }, [editTenant]);

  useEffect(() => {
    if (createTenant.data) {
      toast.success("Tenant created successfully — the owner can sign in now.");
      dispatch(tenantActions.resetCreateTenant());
      setCreateModalOpen(false);
      getTenantList(1);
    } else if (createTenant.error) {
      toast.error(`Error: ${createTenant.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTenant]);

  const getTenantList = (page: number) =>
    dispatch(
      tenantActions.getTenantsListRequest({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      })
    );

  const handleUserStatus = (userId: string, status: "active" | "suspended") => {
    const payload = {
      _id: userId,
      tenantStatus: status,
    };
    dispatch(tenantActions.editTenantRequest(payload));
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirm = () => {
    if (statusRow?._id) {
      handleUserStatus(
        statusRow?._id,
        statusRow?.tenantStatus === "active" ? "suspended" : "active"
      );
    }
  };

  const handlePageChange = (page: number) => {
    getTenantList(page);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    dispatch(tenantActions.resetCreateTenant());
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Tenants | Invyntra",
        description:
          "Track and manage all tenant details with comprehensive information.",
        keywords: "tenants, details, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <Building2 size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Tenants
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage all tenants on the platform.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button rounded="full" onClick={() => setCreateModalOpen(true)}>
              <Plus size={16} className="mr-1" /> Add Tenant
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search tenants..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Status"
                value={statusFilter}
                onChange={setStatusFilter}
                allLabel="All statuses"
                options={STATUS_OPTIONS}
              />
            </div>
          )}
        </Card>

        <Card>
          {/* Table */}
          <TableV2
            columns={[
              {
                key: "tenantId",
                header: "Tenant ID",
                accessor: ({ row, key }) =>
                  row[key] ? (
                    <Link
                      className="underline"
                      to={`/admin/tenant/${row?._id}/details`}
                    >
                      #{row[key]}
                    </Link>
                  ) : (
                    "-"
                  ),
              },
              {
                key: "tenantName",
                header: "Tenant Name",
                width: "100px",
              },
              { key: "tenantEmail", header: "Email" },
              {
                key: "tenantAddressLine1",
                header: "Address",
                accessor: ({ row }) =>
                  formatAddress({
                    addressLine1: row.tenantAddressLine1,
                    addressLine2: row.tenantAddressLine2,
                    city: row.tenantCity,
                    state: row.tenantState,
                    pincode: row.tenantPincode,
                  }) || "-",
              },
              {
                key: "createdAt",
                header: "Joined On",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD/MM/YYYY")}</div>
                ),
              },
              {
                key: "tenantStatus",
                header: "Status",
                accessor: ({ key, row }) => (
                  <Badge
                    text={row?.[key] || ""}
                    color={
                      row?.[key] === "active"
                        ? "green"
                        : row?.[key] === "draft"
                        ? "yellow"
                        : "red"
                    }
                  />
                ),
              },
              {
                key: "planName",
                header: "Plan Name",
                accessor: ({ row }) => {
                  const activePlan = getActivePlan(row?.plans);
                  const planLabel = activePlan?.planName;
                  const planCode = activePlan?.planCode || activePlan?.planId;

                  return planLabel || planCode;
                },
              },
              {
                key: "planStatus",
                header: "Plan Status",
                accessor: ({ row }) => {
                  const activePlan = getActivePlan(row?.plans);
                  const planStatus = getPlanStatus(activePlan?.planEndsAt);

                  return (
                    <Badge
                      text={planStatus}
                      color={planStatus === "active" ? "green" : "red"}
                    />
                  );
                },
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ key, row }) => (
                  <div className="pr-2">
                    <button
                      className="p-2 rounded-md hover:bg-gray-100 text-gray-600"
                      onClick={() => navigate(`/admin/tenant/${row._id}/details`)}
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                      onClick={() => navigate(`/admin/tenant/${row._id}/edit`)}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      className={`p-2 rounded-md ${
                        row.tenantStatus === "active"
                          ? "hover:bg-red-100 text-red-600"
                          : "hover:bg-green-100 text-green-600"
                      }`}
                      onClick={() => {
                        setStatusRow(row);
                        setShowStatusModal(true);
                      }}
                    >
                      <Power size={14} />
                    </button>
                  </div>
                ),
              },
            ]}
            data={tenantList?.data?.data}
            pagination={tenantList?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            statusRow?.tenantStatus === "active" ? "suspend" : "activate"
          } this tenant?`}
          onConfirm={onConfirm}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editTenant?.loading}
        />
      )}

      {createModalOpen && (
        <Modal
          open={createModalOpen}
          onClose={handleCloseCreateModal}
          title="Add Tenant"
          size="lg"
        >
          <TenantCreateWizard
            loading={createTenant.loading}
            onSubmit={(data) => dispatch(tenantActions.createTenantRequest(data))}
          />
        </Modal>
      )}
    </Layout>
  );
};

export default TenantListPage;
